import { Variants } from 'framer-motion';

// Standard timing presets
export const timing = {
  fast: 0.15,
  normal: 0.3,
  slow: 0.5,
} as const;

// Easing presets
export const easing = {
  easeOut: [0.25, 0.1, 0.25, 1] as const,
  easeIn: [0.4, 0, 1, 1] as const,
  easeInOut: [0.4, 0, 0.2, 1] as const,
  spring: { type: 'spring', stiffness: 300, damping: 30 } as const,
};

// Fade in animation
export const fadeIn: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: timing.normal, ease: easing.easeOut },
  },
  exit: {
    opacity: 0,
    transition: { duration: timing.fast, ease: easing.easeIn },
  },
};

// Slide up animation (most common)
export const slideUp: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.35, ease: easing.easeOut },
  },
  exit: {
    opacity: 0,
    y: -10,
    transition: { duration: timing.fast, ease: easing.easeIn },
  },
};

// Slide down animation
export const slideDown: Variants = {
  hidden: { opacity: 0, y: -20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.35, ease: easing.easeOut },
  },
  exit: {
    opacity: 0,
    y: 10,
    transition: { duration: timing.fast, ease: easing.easeIn },
  },
};

// Slide from left
export const slideLeft: Variants = {
  hidden: { opacity: 0, x: -20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: timing.normal, ease: easing.easeOut },
  },
  exit: {
    opacity: 0,
    x: -10,
    transition: { duration: timing.fast, ease: easing.easeIn },
  },
};

// Slide from right
export const slideRight: Variants = {
  hidden: { opacity: 0, x: 20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: timing.normal, ease: easing.easeOut },
  },
  exit: {
    opacity: 0,
    x: 10,
    transition: { duration: timing.fast, ease: easing.easeIn },
  },
};

// Scale in animation
export const scaleIn: Variants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.25, ease: easing.easeOut },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    transition: { duration: timing.fast },
  },
};

// Scale up animation (for emphasis)
export const scaleUp: Variants = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.35, ease: easing.easeOut },
  },
  exit: {
    opacity: 0,
    scale: 0.9,
    transition: { duration: timing.fast },
  },
};

// Stagger container
export const staggerContainer: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
      delayChildren: 0.1,
    },
  },
};

// Stagger item
export const staggerItem: Variants = {
  hidden: { opacity: 0, y: 15 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: timing.normal, ease: easing.easeOut },
  },
};

// Stagger item with scale
export const staggerItemScale: Variants = {
  hidden: { opacity: 0, y: 10, scale: 0.98 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: timing.normal, ease: easing.easeOut },
  },
};

// Hover scale effect
export const hoverScale = {
  scale: 1.02,
  transition: { duration: timing.fast, ease: easing.easeOut },
} as const;

// Tap scale effect
export const tapScale = {
  scale: 0.98,
  transition: { duration: 0.1 },
};

// Page transition variants
export const pageTransition: Variants = {
  initial: { opacity: 0, y: 8 },
  enter: {
    opacity: 1,
    y: 0,
    transition: {
      duration: timing.normal,
      ease: easing.easeOut,
    },
  },
  exit: {
    opacity: 0,
    transition: {
      duration: timing.fast,
      ease: easing.easeIn,
    },
  },
};

// Modal/Dialog variants
export const modalOverlay: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0.2 },
  },
  exit: {
    opacity: 0,
    transition: { duration: timing.fast },
  },
};

export const modalContent: Variants = {
  hidden: { opacity: 0, scale: 0.95, y: 10 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 300,
      damping: 25,
    },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    transition: { duration: timing.fast },
  },
};

// Card hover variants
export const cardHover = {
  rest: {
    scale: 1,
    boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
  },
  hover: {
    scale: 1.02,
    boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
    transition: { duration: timing.fast, ease: easing.easeOut },
  },
  tap: {
    scale: 0.99,
  },
};

// List item variants for AnimatePresence
export const listItem: Variants = {
  hidden: { opacity: 0, x: -10 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.2 },
  },
  exit: {
    opacity: 0,
    x: 10,
    transition: { duration: timing.fast },
  },
};

// Checkmark animation for success states
export const checkmark: Variants = {
  hidden: { pathLength: 0, opacity: 0 },
  visible: {
    pathLength: 1,
    opacity: 1,
    transition: {
      pathLength: { duration: 0.4, ease: 'easeOut' },
      opacity: { duration: 0.1 },
    },
  },
};

// Number counter animation helper
export const counterSpring = {
  type: 'spring' as const,
  stiffness: 100,
  damping: 15,
};
